import { useContext, useState, useEffect, useMemo } from "react";
import { UxAvatarEnum, UxBackgroundEnum } from "@imaginelearning/mps-ux-component-library";
import { UserContext } from "../context-providers/user.provider";

const useUser = () => {
  // returns the userdetails, id and the language the student would prefer to use for the current subject
  const { user, userId, language } = useContext(UserContext);
  const [contentTypeForAlps, setContentTypeForAlps] = useState<{ type: string | undefined } | undefined>(undefined);

  useEffect(() => {
    if (!user?.schoolYear) return;

    const shouldShowLessonsOnly = user.schoolYear >= 2026;

    // Only update state if the value needs to change
    const newType = shouldShowLessonsOnly ? "Lesson" : "";
    if (!contentTypeForAlps || contentTypeForAlps?.type !== newType) {
      setContentTypeForAlps({ type: newType });
    }
  }, [user, contentTypeForAlps]);

  const gradeBandCalc = () => {
    if (user?.gradeLevel < 3) {
      return "K-2";
    }
    if (user?.gradeLevel < 6) {
      return "3-5";
    }
    if (user?.gradeLevel < 9) {
      return "6-8";
    }
    return "9-12";
  };

  const languageSettings = useMemo(
    () => ({
      tts: {
        math: user?.textToSpeech?.includes("MyPathMath") ? "on" : "off",
        reading: user?.textToSpeech?.includes("MyPathReading") ? "on" : "off"
      },
      st: {
        math: user?.languageTranslation?.includes("MyPathMath") ? "on" : "off",
        reading: user?.languageTranslation?.includes("MyPathReading") ? "on" : "off"
      },
      lang: {
        math: user?.spanishLanguage?.includes("MyPathMath") ? "on" : "off",
        reading: user?.spanishLanguage?.includes("MyPathReading") ? "on" : "off"
      }
    }),
    [user?.textToSpeech, user?.languageTranslation, user?.spanishLanguage]
  );

  const getSelectedBackground = (): UxBackgroundEnum => {
    let backgroundValue = user?.selectedBackground ?? user?.background;
    if (backgroundValue) {
      backgroundValue = backgroundValue.toUpperCase();
    }
    const matchedBackground = Object.values(UxBackgroundEnum).find((value) => value.toUpperCase() === backgroundValue);
    return matchedBackground ?? UxBackgroundEnum.DEFAULT;
  };
  const getSelectedAvatar = (): UxAvatarEnum => {
    let avatarValue = user?.selectedAvatar ?? user?.avatar;
    if (avatarValue) {
      avatarValue = avatarValue.toUpperCase();
    }
    const matchedAvatar = Object.values(UxAvatarEnum).find((value) => value.toUpperCase() === avatarValue);
    return matchedAvatar ?? UxAvatarEnum.JOE;
  };

  const gradeBand = gradeBandCalc();
  const avatar = getSelectedAvatar();
  const background = getSelectedBackground();

  return { user, userId, language, languageSettings, gradeBand, avatar, background, contentTypeForAlps };
};

export default useUser;
